import type { AgentTaskModel } from "@fencyai/js";
import { useAgentTasks } from "@fencyai/react";
import { useCallback, useState } from "react";
import type { ContractAutofillContextDto } from "../../autogen/bff-api";
import { useMarkdownEditingV2 } from "./useMarkdownEditingV2";

const MODEL: AgentTaskModel = "anthropic/claude-sonnet-4.6";

const buildPlanMessages = (documentMarkdown: string, context: ContractAutofillContextDto) => [
  {
    role: "SYSTEM" as const,
    content: [
      "You are an assistant helping fill in a contract document for Norwegian public procurement.",
      "You receive a contract document in Markdown and structured data about the contract, buyer organization, and counterparty.",
      "Identify placeholders, blanks, bracketed fields, or obvious missing values in the document.",
      "Only include items where the available data contains a suitable replacement value.",
      "Do NOT list placeholders where no matching data exists — simply skip them.",
      "For each item, use this exact format:",
      "",
      "**Name of data point**",
      "Short description if necessary",
      "Plassholder: `exact placeholder text from the document`",
      "Verdi:",
      "- The replacement value",
      "",
      'IMPORTANT: "Plassholder:" must show the EXACT string as it appears in the document (e.g. `[HE firmanavn]`, `[Fyll ut]`, `[dd.mm.yyyy]`). Copy it character-for-character so the apply step can find and replace it.',
      'IMPORTANT: "Verdi:" must be on its own line, and each value must be a bullet point on the NEXT line below it.',
      "Output only Markdown. Write in the same language as the document (typically Norwegian).",
    ].join("\n"),
  },
  {
    role: "USER" as const,
    content: [`## Document\n${documentMarkdown}`, `## Available contract data\n${JSON.stringify(context, null, 2)}`].join(
      "\n\n"
    ),
  },
];

const extractStreamingText = (result: Awaited<ReturnType<ReturnType<typeof useAgentTasks>["createAgentTask"]>>) => {
  if (result.type !== "success") return null;
  if (result.response.taskType !== "StreamingChatCompletion") return null;
  if (result.response.eventType !== "Completed") return null;
  return result.response.response.textResponse;
};

type UseAutofillProps = {
  fetchCreateStreamingChatCompletionClientToken: () => Promise<{ clientToken: string }>;
  fetchCreateStructuredChatCompletionClientToken: () => Promise<{ clientToken: string }>;
};

export const useAutofill = ({
  fetchCreateStreamingChatCompletionClientToken,
  fetchCreateStructuredChatCompletionClientToken,
}: UseAutofillProps) => {
  const [planMarkdown, setPlanMarkdown] = useState<string | null>(null);
  const [filledDocumentMarkdown, setFilledDocumentMarkdown] = useState<string | null>(null);
  const [filledDocumentVersion, setFilledDocumentVersion] = useState(0);
  const [isGeneratingPlan, setIsGeneratingPlan] = useState(false);
  const [hasError, setHasError] = useState(false);

  const { applyEdits, isEditing, clearEditError } = useMarkdownEditingV2({
    fetchCreateStructuredChatCompletionClientToken,
  });

  const fetchToken = useCallback(
    async (taskType: "StreamingChatCompletion" | "StructuredChatCompletion" | "MemoryChat" | "MemorySearch") => {
      void taskType;
      return fetchCreateStreamingChatCompletionClientToken();
    },
    [fetchCreateStreamingChatCompletionClientToken]
  );

  const { createAgentTask, agentTasks } = useAgentTasks({
    fetchCreateAgentTaskClientToken: fetchToken,
  });

  const generatePlan = useCallback(
    async (documentMarkdown: string, context: ContractAutofillContextDto) => {
      setIsGeneratingPlan(true);
      setHasError(false);
      setPlanMarkdown(null);
      setFilledDocumentMarkdown(null);
      setFilledDocumentVersion(0);
      clearEditError();
      try {
        const result = await createAgentTask({
          type: "StreamingChatCompletion",
          model: MODEL,
          messages: buildPlanMessages(documentMarkdown, context),
        });
        const text = extractStreamingText(result);
        if (!text) {
          setHasError(true);
          return;
        }
        setPlanMarkdown(text);
      } catch {
        setHasError(true);
      } finally {
        setIsGeneratingPlan(false);
      }
    },
    [clearEditError, createAgentTask]
  );

  const applyPlan = useCallback(
    async (documentMarkdown: string, confirmedPlanMarkdown: string) => {
      setHasError(false);
      setFilledDocumentMarkdown(null);
      setFilledDocumentVersion(0);
      clearEditError();

      const result = await applyEdits(documentMarkdown, confirmedPlanMarkdown);
      if (!result) {
        setHasError(true);
        return;
      }

      setFilledDocumentMarkdown(result);
      setFilledDocumentVersion((version) => version + 1);
    },
    [applyEdits, clearEditError]
  );

  const reset = useCallback(() => {
    setPlanMarkdown(null);
    setFilledDocumentMarkdown(null);
    setFilledDocumentVersion(0);
    setHasError(false);
    clearEditError();
  }, [clearEditError]);

  return {
    generatePlan,
    applyPlan,
    reset,
    planMarkdown,
    setPlanMarkdown,
    filledDocumentMarkdown,
    filledDocumentVersion,
    isGeneratingPlan,
    isApplyingPlan: isEditing,
    hasError,
    planAgentTask: agentTasks[0],
    applyAgentTask: agentTasks[1],
    latestAgentTask: agentTasks.at(-1),
  };
};
